import { Controller } from "@hotwired/stimulus"

// チャット形式の職務経歴書ウィザード（参考Reactコード shokumu-keirekisho-tool.jsx の移植）。
// クライアント主導でステップ遷移し、各回答を PATCH save で自動保存する。
// リロード時はサーバが埋め込んだ data / current_step から再開する。
//
// 保存プロトコル:
//   トップレベル:   { current_step, patch: {name: ...} }
//   ジョブ新規追加: { current_step, add_job: true, job_patch: {company: ...} }
//   ジョブ更新:     { current_step, job_index, job_patch: {...} }
export default class extends Controller {
  static targets = [
    "initialData", "messages",
    "phaseCircle", "phaseLabel", "phaseBar",
    "textBlock", "textInput", "textSkip",
    "areaBlock", "areaInput", "areaSkip",
    "periodBlock", "startYear", "startMonth", "endYear", "endMonth", "currentCheck", "currentLabel", "endSelects",
    "choiceBlock", "moreBlock", "confirmBlock", "confirmCount",
    "loading"
  ]
  static values = { saveUrl: String, generateUrl: String, documentUrl: String, initialStep: String }

  // ステップ定義: 入力UI種別と質問文
  static STEPS = {
    name:             { phase: 0, type: "text" },
    job_company:      { phase: 1, type: "text" },
    job_period:       { phase: 1, type: "period" },
    job_employment:   { phase: 1, type: "choice" },
    job_business:     { phase: 1, type: "text", skippable: true },
    job_duties:       { phase: 1, type: "area", rows: 5 },
    job_achievements: { phase: 1, type: "area", rows: 3, skippable: true },
    job_more:         { phase: 1, type: "more" },
    qualifications:   { phase: 2, type: "area", rows: 3, skippable: true },
    pr_appeal:        { phase: 3, type: "text", skippable: true },
    confirm:          { phase: 4, type: "confirm" }
  }

  static PLACEHOLDERS = {
    name: "例：山田 太郎",
    job_company: "例：株式会社サンプル",
    job_business: "例：法人向けSaaSの開発・販売",
    job_duties: "例）\n・法人顧客への新規開拓営業\n・既存顧客のフォローと契約更新\n・若手メンバーの育成",
    job_achievements: "例：新規顧客を年間30社開拓、部門売上 前年比120%達成",
    qualifications: "例）\n普通自動車第一種運転免許（2018年3月）\nTOEIC 800点（2022年5月）",
    pr_appeal: "例：粘り強い課題解決力と、チームをまとめる調整力"
  }

  connect() {
    this.resume = JSON.parse(this.initialDataTarget.textContent || "{}")
    this.resume.jobs ||= []
    this.step = this.initialStepValue || "name"

    if (this.step === "name" && !this.resume.name) {
      this.say("こんにちは。\nこれから職務経歴書の作成に必要なことを、順番にお伺いします。\nわかる範囲で回答してください 📄")
      this.say("まずはお名前を教えてください。（例：山田 太郎）")
    } else {
      this.say("おかえりなさい。前回の続きから再開します。")
      const prompt = this.promptFor(this.step)
      if (prompt) this.say(prompt)
    }
    this.showInput(this.step)
  }

  // --- ステップごとの質問文（参考コードの promptFor を移植） ---

  promptFor(step) {
    const n = this.resume.jobs.length + 1
    const co = this.currentJob()?.company || "その会社"
    switch (step) {
      case "name": return "お名前を教えてください。（例：山田 太郎）"
      case "job_company":
        return n === 1
          ? "ありがとうございます。ここから職務経歴を伺います。\n直近（現在または最後）に在籍した会社名を教えてください。\n「株式会社」「有限会社」などの法人格も含めた正式名称でお願いします。"
          : `次に、その前に在籍した会社名を教えてください。（${n}社目）\n法人格を含めた正式名称でお願いします。`
      case "job_period": return `「${co}」での在籍期間を選んでください。`
      case "job_employment": return "雇用形態を選んでください。"
      case "job_business": return `「${co}」の事業内容を一言で教えてください。（任意・スキップ可）`
      case "job_duties": return `「${co}」での業務内容を教えてください。\n箇条書き・ざっくりで大丈夫です。あとでAIが整えます ✍️`
      case "job_achievements": return "主な実績・成果があれば教えてください。（任意・スキップ可）\n数字を入れると効果的です。"
      case "job_more": return `「${co}」の入力が完了しました。さらに前の職歴を追加しますか？`
      case "qualifications": return "保有資格があれば1行ずつ入力してください。（任意・スキップ可）\n例：日商簿記2級（2020年6月）"
      case "pr_appeal": return "最後に、アピールしたい強みを一言で教えてください。\nこれを元にAIが自己PRを作成します。（任意・スキップ可）"
      case "confirm": return "おつかれさまでした！内容がそろいました。\nAIが「職務要約」「活かせるスキル」「自己PR」を自動作成します 🪄"
      default: return ""
    }
  }

  currentJob() {
    return this.resume.jobs[this.resume.jobs.length - 1]
  }

  // --- 入力ハンドラ ---

  submitText(event) {
    if (event.type === "keydown" && event.isComposing) return // IME確定のEnterは無視
    event.preventDefault?.()
    const value = this.textInputTarget.value.trim()
    if (!value) return
    this.userSay(value)
    this.textInputTarget.value = ""

    switch (this.step) {
      case "name":
        this.resume.name = value
        this.goTo("job_company", { patch: { name: value } })
        break
      case "job_company":
        this.resume.jobs.push({ company: value })
        this.goTo("job_period", { add_job: true, job_patch: { company: value } })
        break
      case "job_business":
        this.currentJob().business = value
        this.goTo("job_duties", this.jobPatch({ business: value }))
        break
      case "pr_appeal":
        this.resume.pr_appeal = value
        this.goTo("confirm", { patch: { pr_appeal: value } })
        break
    }
  }

  submitArea() {
    const value = this.areaInputTarget.value.trim()
    if (!value) return
    this.userSay(value.length > 60 ? value.slice(0, 60) + "…" : value)
    this.areaInputTarget.value = ""

    switch (this.step) {
      case "job_duties":
        this.currentJob().duties = value
        this.goTo("job_achievements", this.jobPatch({ duties: value }))
        break
      case "job_achievements":
        this.currentJob().achievements = value
        this.goTo("job_more", this.jobPatch({ achievements: value }))
        break
      case "qualifications":
        this.resume.qualifications = value
        this.goTo("pr_appeal", { patch: { qualifications: value } })
        break
    }
  }

  // 任意項目のスキップ
  skip() {
    this.userSay("スキップ")
    switch (this.step) {
      case "job_business":
        this.goTo("job_duties", this.jobPatch({ business: "" }))
        break
      case "job_achievements":
        this.goTo("job_more", this.jobPatch({ achievements: "" }))
        break
      case "qualifications":
        this.goTo("pr_appeal", { patch: { qualifications: "" } })
        break
      case "pr_appeal":
        this.goTo("confirm", { patch: { pr_appeal: "" } })
        break
    }
  }

  toggleCurrent() {
    const current = this.currentCheckTarget.checked
    this.currentLabelTarget.classList.toggle("hidden", !current)
    this.endSelectsTarget.classList.toggle("hidden", current)
  }

  submitPeriod() {
    const pad = (m) => String(m).padStart(2, "0")
    const current = this.currentCheckTarget.checked
    const period = {
      start: `${this.startYearTarget.value}-${pad(this.startMonthTarget.value)}`,
      end: current ? "" : `${this.endYearTarget.value}-${pad(this.endMonthTarget.value)}`,
      current: current
    }
    const startLabel = `${this.startYearTarget.value}年${Number(this.startMonthTarget.value)}月`
    const endLabel = current ? "現在" : `${this.endYearTarget.value}年${Number(this.endMonthTarget.value)}月`
    this.userSay(`${startLabel} ～ ${endLabel}`)

    this.currentJob().period = period
    this.goTo("job_employment", this.jobPatch({ period: period }))
  }

  submitEmployment(event) {
    const value = event.currentTarget.dataset.value
    this.userSay(value)
    this.currentJob().employment = value
    this.goTo("job_business", this.jobPatch({ employment: value }))
  }

  addJob() {
    this.userSay("もう1社追加する")
    this.goTo("job_company", {})
  }

  continueNext() {
    this.userSay("職歴の入力を終えて次へ進む")
    this.goTo("qualifications", {})
  }

  async generate() {
    this.loadingTarget.classList.remove("hidden")
    this.loadingTarget.classList.add("flex")
    try {
      const response = await fetch(this.generateUrlValue, {
        method: "POST",
        headers: { "Accept": "application/json", "X-CSRF-Token": this.csrfToken() }
      })
      if (!response.ok) throw new Error()
      window.Turbo ? window.Turbo.visit(this.documentUrlValue) : (window.location.href = this.documentUrlValue)
    } catch {
      this.loadingTarget.classList.add("hidden")
      this.loadingTarget.classList.remove("flex")
      this.say("作成に失敗しました。少し時間をおいて、もう一度お試しください。")
    }
  }

  // --- 進行・保存 ---

  jobPatch(patch) {
    return { job_index: this.resume.jobs.length - 1, job_patch: patch }
  }

  goTo(step, savePayload) {
    this.step = step
    this.save({ current_step: step, ...savePayload })
    const prompt = this.promptFor(step)
    if (prompt) this.say(prompt)
    this.showInput(step)
  }

  async save(payload) {
    try {
      await fetch(this.saveUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify(payload)
      })
    } catch {
      // 自動保存の失敗は会話を止めない（次の保存でcurrent_stepごと回復する）
    }
  }

  // --- UI ---

  showInput(step) {
    const config = this.constructor.STEPS[step]
    if (!config) return

    const blocks = {
      text: this.textBlockTarget, area: this.areaBlockTarget, period: this.periodBlockTarget,
      choice: this.choiceBlockTarget, more: this.moreBlockTarget, confirm: this.confirmBlockTarget
    }
    Object.values(blocks).forEach((el) => { el.classList.add("hidden"); el.classList.remove("flex") })

    const block = blocks[config.type]
    block.classList.remove("hidden")
    if ([ "choice", "more", "confirm" ].includes(config.type)) block.classList.add("flex")

    if (config.type === "text") {
      this.textInputTarget.placeholder = this.constructor.PLACEHOLDERS[step] || ""
      this.textSkipTarget.classList.toggle("hidden", !config.skippable)
      this.textInputTarget.focus()
    } else if (config.type === "area") {
      this.areaInputTarget.placeholder = this.constructor.PLACEHOLDERS[step] || ""
      this.areaInputTarget.rows = config.rows || 4
      this.areaSkipTarget.classList.toggle("hidden", !config.skippable)
      this.areaInputTarget.focus()
    } else if (config.type === "period") {
      // 1社目は「在籍中」を初期ON（直近の会社から入力するため）
      this.currentCheckTarget.checked = this.resume.jobs.length === 1
      this.toggleCurrent()
    } else if (config.type === "confirm") {
      this.confirmCountTarget.textContent = this.resume.jobs.length
    }

    this.updateStepper(config.phase)
  }

  updateStepper(activePhase) {
    this.phaseCircleTargets.forEach((el) => {
      const phase = Number(el.dataset.phase)
      const done = phase < activePhase
      const active = phase === activePhase
      el.classList.toggle("bg-kk-navy", done)
      el.classList.toggle("text-white", done)
      el.classList.toggle("bg-white", !done)
      el.classList.toggle("border-kk-navy", done || active)
      el.classList.toggle("border-kk-line", !done && !active)
      el.classList.toggle("text-kk-navy", active && !done)
      el.classList.toggle("text-kk-sub", !active && !done)
      el.textContent = done ? "✓" : String(phase + 1)
    })
    this.phaseLabelTargets.forEach((el) => {
      const phase = Number(el.dataset.phase)
      el.classList.toggle("font-bold", phase === activePhase)
      el.classList.toggle("text-kk-navy-deep", phase <= activePhase)
      el.classList.toggle("text-kk-sub", phase > activePhase)
    })
    this.phaseBarTargets.forEach((el) => {
      const phase = Number(el.dataset.phase)
      el.classList.toggle("bg-kk-navy", phase < activePhase)
      el.classList.toggle("bg-kk-line", phase >= activePhase)
    })
  }

  say(text) {
    const wrapper = document.createElement("div")
    wrapper.className = "kk-fade flex justify-start"
    wrapper.innerHTML = `
      <div class="mr-2 mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-kk-navy-soft">
        <span class="text-sm">✨</span>
      </div>
      <div class="max-w-[78%] whitespace-pre-wrap rounded-2xl rounded-tl-[4px] border border-kk-line bg-white px-4 py-2.5 text-sm leading-relaxed text-kk-ink"></div>`
    wrapper.lastElementChild.textContent = text
    this.messagesTarget.appendChild(wrapper)
    this.scrollToBottom()
  }

  userSay(text) {
    const wrapper = document.createElement("div")
    wrapper.className = "kk-fade flex justify-end"
    wrapper.innerHTML = `
      <div class="max-w-[78%] whitespace-pre-wrap rounded-2xl rounded-tr-[4px] bg-kk-navy px-4 py-2.5 text-sm leading-relaxed text-white"></div>`
    wrapper.firstElementChild.textContent = text
    this.messagesTarget.appendChild(wrapper)
    this.scrollToBottom()
  }

  scrollToBottom() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
